import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/UsageEventsService';
import {useEffect, useState} from 'react';

// FNP-536: read-only module. No create/update/delete — the FNP-215 ingestion writer
// owns the UsageEvent lifecycle. This hook is list-fetch only.
//
// Module-scoped global filter store (global-filter-store-pattern). Backend-supported
// fields (UsageEventsFilterInput, FNP-594):
//   accountId          -> ID
//   customerId         -> ID
//   meterCode          -> String
//   unresolvedCustomer -> Boolean ("true" selects the who-pays-unknown rows)
//   unmetered          -> Boolean ("true" selects the unknown-meter rows)
// No default filter is seeded — admins see all events newest-first on first visit.
let globalFilters: Record<string, string> = {};
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useUsageEventsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {filters, handleSetFilters, handleClearFilters};
};

export const useUsageEvents = () => {
    const {filters} = useUsageEventsFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = {column: 'created', direction: 'desc' as const};

    // INFINITE QUERY — the filter object is part of the cache key, so changing the
    // filter refetches automatically.
    const usageEventsQuery = useInfiniteQuery({
        queryKey: ['usageEvents', keyword, filters, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, sort, filter: filters, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (usageEventsQuery.hasNextPage) {
            usageEventsQuery.fetchNextPage();
        }
    };

    const reload = () => {
        usageEventsQuery.refetch();
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    // FNP-539: the service guarantees page.items is a real array even on a GraphQL-level
    // error; filter(Boolean) keeps an undefined row out of the table as defense-in-depth.
    const allItems = (usageEventsQuery.data?.pages?.flatMap(page => page?.items ?? []) || []).filter(Boolean);

    return {
        items: allItems,
        visibleItems: allItems,
        loading: usageEventsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: usageEventsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reload,
        handleFilterByKeyword,
    };
};
